import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useBusinessStore } from '../store/useBusinessStore';
import { useAuthStore } from '../store/useAuthStore';
import { supabase } from '../supabaseClient';
import { DEFAULT_SETTINGS } from '../utils/quotes';
import { linesToSaleItems } from '../utils/sales';

const DEMO_BUSINESS_IDS = ['9001', '9002', '9003'];
const PAGE = 1000;
const LIST_LIMIT = 500;

const isMissingTable = (error) =>
  ['42P01', 'PGRST205', '42883', 'PGRST202'].includes(error?.code) || /sales|payments|create_sale|register_payment/i.test(error?.message || '');

// Los mensajes de las reglas de la base ya están en español; un rechazo de permisos viene en inglés y se cambia por una frase clara.
const explain = (error, fallback) => {
  if (error?.code === '42501' && /row-level security|permission denied/i.test(error.message || '')) return 'No tienes permiso sobre las ventas de este negocio (solo el dueño o un administrador).';
  if (error?.code === '23505') return 'Esa cotización ya tiene una venta.';
  return ['22023', '42501'].includes(error?.code) && error?.message ? error.message : fallback;
};

const fetchAll = async (build) => {
  const rows = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await build().range(from, from + PAGE - 1);
    if (error) return { error };
    rows.push(...(data || []));
    if (!data || data.length < PAGE) return { data: rows };
  }
};

// Ventas, abonos y cartera. Todo lo que cambia se hace llamando a funciones de la base (create_sale, register_payment, void_payment,
// void_sale): ellas calculan, descuentan existencias y validan; la app solo muestra el resultado.
export const useSales = ({ withList = true, withCustomers = true } = {}) => {
  const { activeBusinessId } = useBusinessStore();
  const { userRole } = useAuthStore();
  const isReadOnlyDemo = DEMO_BUSINESS_IDS.includes(String(activeBusinessId)) && userRole !== 'master';

  const [sales, setSales] = useState([]);
  const [openSales, setOpenSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [quotesById, setQuotesById] = useState(new Map());
  const [settings, setSettings] = useState({ ...DEFAULT_SETTINGS });
  const [loading, setLoading] = useState(true);
  const [tableMissing, setTableMissing] = useState(false);

  const load = useCallback(async () => {
    if (!activeBusinessId) { setLoading(false); return; }
    setLoading(true);
    const business = String(activeBusinessId);
    const [list, open, cust, set] = await Promise.all([
      withList
        ? supabase.from('sales').select('*').eq('business_id', business).order('created_at', { ascending: false }).range(0, LIST_LIMIT - 1)
        : Promise.resolve({ data: [], error: null }),
      withList
        ? fetchAll(() => supabase.from('sales').select('*').eq('business_id', business).in('status', ['por_cobrar', 'parcial']).order('created_at'))
        : Promise.resolve({ data: [] }),
      withCustomers ? fetchAll(() => supabase.from('customers').select('*').eq('business_id', business).order('name')) : Promise.resolve({ data: [] }),
      supabase.from('billing_settings').select('*').eq('business_id', business).maybeSingle()
    ]);
    const error = list.error || open.error || set.error;
    if (error) {
      setTableMissing(isMissingTable(error));
      setSales([]); setOpenSales([]);
    } else {
      setTableMissing(false);
      setSales(list.data || []);
      setOpenSales(open.data || []);
      setSettings({ ...DEFAULT_SETTINGS, ...(set.data || {}) });
      // Las cotizaciones de origen (número y versión) para mostrarlas y exportarlas.
      const ids = [...new Set((list.data || []).map((s) => s.quote_id).filter(Boolean))];
      if (ids.length) {
        const { data } = await supabase.from('quotes').select('id, number, version').in('id', ids.slice(0, 500));
        setQuotesById(new Map((data || []).map((q) => [q.id, q])));
      } else {
        setQuotesById(new Map());
      }
    }
    setCustomers(cust.error ? [] : cust.data);
    setLoading(false);
  }, [activeBusinessId, withList, withCustomers]);

  useEffect(() => { load(); }, [load]);

  const guardDemo = () => {
    if (isReadOnlyDemo) { toast.error('Modo solo lectura: las cuentas demo no se pueden modificar.'); return true; }
    return false;
  };

  // Una venta con sus líneas, abonos, movimientos de existencias y la cotización de origen.
  const getSale = useCallback(async (id) => {
    const business = String(activeBusinessId);
    const sale = await supabase.from('sales').select('*').eq('id', id).eq('business_id', business).maybeSingle();
    if (sale.error) { toast.error('No se pudo abrir la venta.'); return null; }
    if (!sale.data) return null;
    const [items, payments, movements, quote] = await Promise.all([
      supabase.from('sale_items').select('*').eq('sale_id', id).order('position'),
      supabase.from('payments').select('*').eq('sale_id', id).order('paid_at'),
      supabase.from('stock_movements').select('*').eq('sale_id', id).order('created_at'),
      sale.data.quote_id ? supabase.from('quotes').select('id, number, version').eq('id', sale.data.quote_id).maybeSingle() : Promise.resolve({ data: null })
    ]);
    if (items.error || payments.error || movements.error) { toast.error('No se pudo abrir la venta.'); return null; }
    return { sale: sale.data, items: items.data || [], payments: payments.data || [], movements: movements.data || [], quote: quote.data || null };
  }, [activeBusinessId]);

  // La venta vigente de una cotización (para mostrar "Ver venta" en vez de "Crear venta").
  const findSaleByQuote = useCallback(async (quoteId) => {
    const { data, error } = await supabase.from('sales').select('id, number, status').eq('quote_id', quoteId).neq('status', 'anulada').maybeSingle();
    return error ? null : data;
  }, []);

  const call = async (fn, args, fallback) => {
    const { data, error } = await supabase.rpc(fn, args);
    if (error) {
      toast.error(['PGRST202', '42883'].includes(error.code) ? 'Falta ejecutar la migración 20261003c_sales.sql en Supabase.' : explain(error, fallback));
      return null;
    }
    return Array.isArray(data) ? data[0] ?? {} : data ?? {};
  };

  // Devuelve { sale_id, number, total, warnings } o null.
  const createSaleFromQuote = async (quoteId, dueDate = null) => {
    if (guardDemo()) return null;
    return call('create_sale', { p_quote: quoteId, p_due_date: dueDate || null }, 'No se pudo crear la venta.');
  };

  const createManualSale = async ({ customer_id, lines, due_date }) => {
    if (guardDemo()) return null;
    return call('create_sale', { p_business: String(activeBusinessId), p_customer: customer_id || null, p_items: linesToSaleItems(lines), p_due_date: due_date || null }, 'No se pudo crear la venta.');
  };

  const registerPayment = async (saleId, { amount, method, reference, paid_at }) => {
    if (guardDemo()) return null;
    const row = await call('register_payment', { p_sale: saleId, p_amount: Number(amount), p_method: method, p_reference: String(reference ?? '').trim() || null, p_paid_at: paid_at || null }, 'No se pudo registrar el abono.');
    if (row) toast.success('Abono registrado');
    return row;
  };

  const voidPayment = async (paymentId, reason) => {
    if (guardDemo()) return null;
    const row = await call('void_payment', { p_payment: paymentId, p_reason: reason }, 'No se pudo anular el abono.');
    if (row) toast.success('Abono anulado');
    return row;
  };

  const voidSale = async (saleId, reason) => {
    if (guardDemo()) return false;
    const row = await call('void_sale', { p_sale: saleId, p_reason: reason }, 'No se pudo anular la venta.');
    if (row) toast.success('Venta anulada y existencias devueltas');
    return Boolean(row);
  };

  // Todos los abonos del negocio (para exportar).
  const fetchAllPayments = async () => {
    const { data, error } = await fetchAll(() => supabase.from('payments').select('*').eq('business_id', String(activeBusinessId)).order('paid_at'));
    if (error) { toast.error('No se pudieron leer los abonos.'); return null; }
    return data;
  };

  return {
    sales, openSales, customers, quotesById, settings, loading, tableMissing, isReadOnlyDemo,
    reload: load, getSale, findSaleByQuote, createSaleFromQuote, createManualSale, registerPayment, voidPayment, voidSale, fetchAllPayments
  };
};
